import React from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { formatDarStatus, formatDate } from "./nocUtils";
import { DnarOption } from "./types";

interface NocDandARCardProps {
  nocData: any;
  nocType: string;
  dnARStatus: string;
  dnARPenaltyEffective: string;
  remarks: string;
  dnarMasterList?: DnarOption[];
  onStatusChange: (status: string) => void;
  onPenaltyEffectiveChange: (date: string) => void;
  onRemarksChange: (remarks: string) => void;
  getFieldStatus: (field: string) => "EDITABLE" | "READONLY" | "HIDDEN";
}

export const NocDandARCard: React.FC<NocDandARCardProps> = ({
  nocData,
  nocType,
  dnARStatus,
  dnARPenaltyEffective,
  remarks,
  dnarMasterList,
  onStatusChange,
  onPenaltyEffectiveChange,
  onRemarksChange,
  getFieldStatus,
}) => {
  const isDnarEditable = getFieldStatus("DandARStatus") === "EDITABLE";
  const isPenaltyDateEditable = getFieldStatus("DandARPenaltyEffective") === "EDITABLE";
  const isRemarksEditable = getFieldStatus("Remarks") === "EDITABLE";

  const currentDnarStatus =
    isDnarEditable
      ? (dnARStatus || "")
      : (dnARStatus ||
        nocData?.dnARStatus ||
        nocData?.DnARStatus ||
        nocData?.dandARStatus ||
        nocData?.DandARStatus ||
        "");

  const penaltyDate =
    dnARPenaltyEffective ||
    nocData?.dnARPenaltyEffecti ||
    nocData?.DnARPenaltyEffecti ||
    nocData?.dandARPenaltyEffective ||
    nocData?.DandARPenaltyEffective ||
    "";

  const controllingRemarks = (() => {
    // "Remarks by Controlling" refers to Controlling Officer / D&AR remarks (entered in "Remarks (if any)")
    const cand =
      nocData?.Remarks ||
      nocData?.remarks ||
      nocData?.dandARRemarks ||
      nocData?.dnarRemarks ||
      "";
    if (
      !cand ||
      cand === "X" ||
      cand === "-" ||
      cand === "null"
    ) {
      return "-";
    }
    return String(cand).trim() || "-";
  })();

  // Module-specific visibility rules matching Mobile Application
  if (nocType === "NOC_EXINDIA") {
    const hasDnarValue = Boolean(currentDnarStatus && currentDnarStatus.trim() !== "");
    if (!isDnarEditable && !hasDnarValue) {
      return null;
    }
  }

  const renderAsForm = nocType === "NOC_PASSPORT" || nocType === "NOC_EXINDIA" || isDnarEditable;

  return (
    <div className="card mb-4 border-0 shadow-sm">
      <div className="card-header bg-light">
        <h5 className="card-title mb-0">
          <i className="ti ti-shield-check me-2 text-primary"></i>
          D&AR Status of the Employee
        </h5>
      </div>
      <div className="card-body">
        <div className="row g-3">
          {renderAsForm ? (
            <>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">
                  D&AR status of the employee:
                  {isDnarEditable && <span className="text-danger"> *</span>}
                </label>
                <select
                  className="form-select"
                  value={currentDnarStatus}
                  disabled={!isDnarEditable}
                  onChange={(e) => {
                    const val = e.target.value;
                    onStatusChange(val);
                    if (val !== "M" && val !== "J") {
                      onPenaltyEffectiveChange("");
                    }
                  }}
                >
                  <option value="">Select Status</option>
                  {dnarMasterList && dnarMasterList.length > 0 ? (
                    dnarMasterList.map((item) => (
                      <option key={item.dnar_id || item.value} value={item.dnar_id || item.value}>
                        {item.dnar_title || item.label}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="N">No D&AR contemplated against the employee</option>
                      <option value="P">D&AR Pending/ Deemed Suspension /Suspension/ Court Case Pending</option>
                      <option value="M">D&AR completed; Minor penalty has been imposed</option>
                      <option value="J">D&AR completed; Major penalty has been imposed</option>
                    </>
                  )}
                </select>
              </div>

              {(currentDnarStatus === "M" || currentDnarStatus === "J") && (
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">
                    D&AR Penalty Effective Date
                    {isPenaltyDateEditable && <span className="text-danger"> *</span>}
                  </label>
                  <DatePicker
                    className="form-control"
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder="Select date"
                    disabled={!isPenaltyDateEditable}
                    onChange={(date) => {
                      if (date && date.isValid()) {
                        onPenaltyEffectiveChange(date.format("YYYY-MM-DD"));
                      } else {
                        onPenaltyEffectiveChange("");
                      }
                    }}
                    value={
                      penaltyDate &&
                        penaltyDate !== "0000-00-00" &&
                        penaltyDate !== "00000000" &&
                        penaltyDate !== "--" &&
                        penaltyDate !== "-" &&
                        dayjs(penaltyDate).isValid()
                        ? dayjs(penaltyDate)
                        : null
                    }
                  />
                </div>
              )}

              {getFieldStatus("Remarks") !== "HIDDEN" && (
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Remarks (if any):</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={isRemarksEditable ? "Enter remarks (if any)" : "-"}
                    value={isRemarksEditable ? remarks : (controllingRemarks !== "-" ? controllingRemarks : "")}
                    disabled={!isRemarksEditable}
                    onChange={(e) => onRemarksChange(e.target.value)}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small
                    className="text-muted d-block text-uppercase fw-medium mb-1"
                    style={{ fontSize: "0.78rem", letterSpacing: "0.04em" }}
                  >
                    DAR Status
                  </small>
                  <div className="fw-bold text-dark fs-15">
                    {currentDnarStatus ? formatDarStatus(currentDnarStatus) : "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small
                    className="text-muted d-block text-uppercase fw-medium mb-1"
                    style={{ fontSize: "0.78rem", letterSpacing: "0.04em" }}
                  >
                    D&AR Penalty Effective
                  </small>
                  <div className="fw-bold text-dark fs-15">
                    {(() => {
                      if (
                        !penaltyDate ||
                        penaltyDate === "0000-00-00" ||
                        penaltyDate === "00000000" ||
                        penaltyDate === "00.00.0000" ||
                        penaltyDate === "-" ||
                        penaltyDate === "--" ||
                        penaltyDate === "Invalid date"
                      )
                        return "-";
                      return formatDate(penaltyDate) || "-";
                    })()}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small
                    className="text-muted d-block text-uppercase fw-medium mb-1"
                    style={{ fontSize: "0.78rem", letterSpacing: "0.04em" }}
                  >
                    Remarks by Controlling
                  </small>
                  <div className="fw-bold text-dark fs-15">{controllingRemarks || "-"}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
